import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Home, Calendar, CreditCard, User, Users, Settings, ClipboardList } from 'lucide-react';

/**
 * Mobile Bottom Navigation
 * Shows only on mobile devices (md:hidden) for authenticated users
 * Provides quick access to main dashboard sections
 */
export const MobileBottomNav = () => {
  const { user, isAuthenticated, isAdmin, isStaff, isTeacher } = useAuth();
  const location = useLocation();

  // Only show for authenticated users
  if (!isAuthenticated || !user) return null;

  // Don't show on public pages
  const publicPages = ['/', '/classes', '/schedule', '/about', '/contact', '/free-trial', '/login', '/register'];
  if (publicPages.includes(location.pathname)) return null;

  // Define navigation items based on user role
  const getNavItems = () => {
    if (isAdmin || isStaff) {
      return [
        { href: '/admin', icon: Home, label: 'Home', testId: 'mobile-nav-home' },
        { href: '/admin/classes', icon: Calendar, label: 'Classes', testId: 'mobile-nav-classes' },
        { href: '/admin/students', icon: Users, label: 'Students', testId: 'mobile-nav-students' },
        { href: '/admin/finance', icon: CreditCard, label: 'Finance', testId: 'mobile-nav-finance' },
        { href: '/admin/settings', icon: Settings, label: 'Settings', testId: 'mobile-nav-settings' },
      ];
    } else if (isTeacher) {
      return [
        { href: '/teacher', icon: Home, label: 'Home', testId: 'mobile-nav-home' },
        { href: '/teacher', icon: Calendar, label: 'Schedule', testId: 'mobile-nav-schedule' },
        { href: '/teacher', icon: ClipboardList, label: 'Register', testId: 'mobile-nav-register' },
        { href: '/teacher', icon: User, label: 'Profile', testId: 'mobile-nav-profile' },
      ];
    } else {
      // Parent
      return [
        { href: '/dashboard', icon: Home, label: 'Home', testId: 'mobile-nav-home' },
        { href: '/dashboard?tab=classes', icon: Calendar, label: 'Classes', testId: 'mobile-nav-classes' },
        { href: '/dashboard?tab=profile', icon: CreditCard, label: 'Billing', testId: 'mobile-nav-billing' },
        { href: '/dashboard?tab=profile', icon: User, label: 'Profile', testId: 'mobile-nav-profile' },
      ];
    }
  };

  const navItems = getNavItems();

  const isActive = (href) => {
    if (href.includes('?')) {
      return location.pathname + location.search === href;
    }
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-black/95 backdrop-blur-lg border-t border-white/10 safe-area-pb"
      data-testid="mobile-bottom-nav"
    >
      <div className="flex justify-around items-center h-16 px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          
          return (
            <Link
              key={item.href}
              to={item.href}
              className={`flex flex-col items-center justify-center flex-1 py-2 transition-colors ${
                active 
                  ? 'text-[#FF007F]' 
                  : 'text-white/60 hover:text-white'
              }`}
              data-testid={item.testId}
            >
              <Icon className={`w-5 h-5 ${active ? 'scale-110' : ''} transition-transform`} />
              <span className={`text-xs mt-1 font-medium ${active ? 'font-semibold' : ''}`}>
                {item.label}
              </span>
              {active && (
                <div className="absolute bottom-0 w-12 h-0.5 bg-[#FF007F] rounded-full" />
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
